import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { Divider } from 'antd';
import cn from 'classnames';
import Actions from './components/Actions/Actions';
import QuestionsList from './components/QuestionsList/QuestionsList';
import QuestionsDrawer from './components/QuestionsDrawer/QuestionsDrawer';
import QuestionBox from './components/QuestionBox/QuestionBox';
import PostMessageForm from './components/PostMessageForm/PostMessageForm';
import {
  isHtmlSelector,
  notificationQuestionSelector,
  selectedQuestionSelector,
} from './store/selectors';
import {
  resetQuestionsStore,
  setNotificationQuestion,
  setQuestionsSavedData,
  setSearchVal,
  setSelectedQuestion,
} from './store/actions';
import { pageStateSelector } from '../../crossPageInteractions/store/selectors';
import { resetPageState } from '../../crossPageInteractions/store/actions';
import { questionOpenStateId } from './constants/common';
import useSetRefreshFn from '../../hooks/useSetRefreshFn';
import { questionsQueryKeys } from './queries/questionsQueryKeys';
import useQuestionsQueryParams from './hooks/useQuestionsQueryParams';
import FromNotificationAlert from '../../components/FromNotificationAlert/FromNotificationAlert';
import useQuestions from './queries/useQuestions';
import styles from './QuestionsContainer.module.scss';
import 'draft-js/dist/Draft.css';

const QuestionsContainer = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { state: locationState } = useLocation();
  const { replace: historyReplace } = useHistory();

  const selectedQuestion = useSelector(selectedQuestionSelector);
  const notificationQuestion = useSelector(notificationQuestionSelector);
  const isHtml = useSelector(isHtmlSelector);
  const questionsPageState = useSelector(pageStateSelector('Questions'));

  const questionsQueryParams = useQuestionsQueryParams();

  const { data: questions, isFetching: isQuestionsFetching } = useQuestions(
    notificationQuestion
      ? { questionNum: Number(notificationQuestion.id) }
      : questionsQueryParams,
    { enabled: Boolean(notificationQuestion?.id || questionsQueryParams) }
  );

  useEffect(() => {
    const isFromNotifications = locationState?.from === 'notifications';

    if (isFromNotifications) {
      dispatch(setNotificationQuestion(locationState));
      dispatch(setSearchVal(''));
      historyReplace({ state: undefined });

      if (questionsPageState) {
        dispatch(resetPageState('Questions'));
      }
    } else {
      if (questionsPageState) {
        dispatch(setQuestionsSavedData(questionsPageState));
        dispatch(resetPageState('Questions'));
      }
    }
  }, [locationState, questionsPageState]);

  useEffect(() => {
    return () => {
      dispatch(resetQuestionsStore());
    };
  }, []);

  useSetRefreshFn(() => {
    let keys = [questionsQueryKeys.questions(questionsQueryParams)];

    if (!notificationQuestion) {
      keys = keys.concat([
        questionsQueryKeys.attachmentList(selectedQuestion?.id),
        questionsQueryKeys.questionMessages(selectedQuestion?.id),
      ]);
    } else {
      dispatch(setNotificationQuestion(undefined));
      dispatch(setSelectedQuestion(null));
    }

    keys.forEach((key) => {
      queryClient.invalidateQueries(key);
    });
  }, [questionsQueryParams, selectedQuestion?.id, notificationQuestion?.id]);

  const handleFromNotificationAlertClose = () => {
    dispatch(setNotificationQuestion(undefined));
    dispatch(setSelectedQuestion(null));
  };

  return (
    <>
      <Actions />
      {Boolean(notificationQuestion) && !isQuestionsFetching && (
        <FromNotificationAlert
          {...notificationQuestion.texts}
          hasData={!isQuestionsFetching && questions?.length > 0}
          onClose={handleFromNotificationAlertClose}
        />
      )}
      <section className={styles.root}>
        <QuestionsList
          className={cn(styles.col, styles.questionListCol)}
          queryParams={questionsQueryParams}
        />
        {selectedQuestion && (
          <>
            <QuestionBox className={cn(styles.col, styles.questionCol)} />
            <Divider type="vertical" className={styles.divider} />
          </>
        )}
        {selectedQuestion?.stateId === questionOpenStateId && isHtml && (
          <PostMessageForm className={cn(styles.editorCol)} />
        )}
        <QuestionsDrawer />
      </section>
    </>
  );
};

export default QuestionsContainer;
